import $ from 'jquery';
import layout from '../Layout';
import ProductForm from './ProductForm';
import Cart from '../components/Cart';
import ScrollLock from '../utils/ScrollLock';

export default class ProductMenu {
  constructor({
    el,
    productEl,
    data,
    postMessage,
    inQuickshop = false,
  }) {
    this.el = el;
    this.$el = $(el);
    this.data = data;
    this.postMessage = postMessage;
    this.minimized = false;

    this.states = {
      selectOptions: 'selectoptions',
      addToCart: 'addtocart',
      addToCartSuccess: 'addtocart-success',
    };

    this.dismissButton = this.el.querySelector('[data-product-menu-slideout-dismiss]');
    this.selectOptionsButton = this.el.querySelector('[data-product-menu-button="selectoptions"]');
    this.addToCartButton = this.el.querySelector('[data-product-menu-button="addtocart"]');
    this.checkoutButton = this.el.querySelector('[data-product-menu-checkout]');

    this.slideout = this.el.querySelector('[data-product-menu-slideout]');
    this.$slideout = $(this.slideout);
    this.defaultDesktopState = this.el.getAttribute('data-product-menu-desktop-default-state');
    this.defaultMobileState = this.el.getAttribute('data-product-menu-mobile-default-state');
    this.currentState = layout.isBreakpoint('L') ? this.defaultDesktopState : this.defaultMobileState;

    // Form
    this.form = new ProductForm(this.$el, {
      product: this.data.product,
      productEl,
      moneyFormat: window.Theme.moneyFormat,
      postMessage,
      useHistory: !inQuickshop,
      enableCartRedirection: this.data.enable_cart_redirection,
      onSuccess: (response, quantity) => this._onAtcSuccess(response, quantity),
      onError: (response, error) => this._onAtcError(response, error),
      onVariantChange: variant => this._onVariantChange(variant),
      onQuantityChange: (variant, quantity) => this._onQuantityChange(variant, quantity),
      onAddToCart: () => this._onAddToCart(),
    });

    // atc button
    this.$productMenuButtonAddToCartPrice = $(
      '[data-product-menu-button-addtocart-price]',
      this.el,
    );

    // Checkout button
    this.$productMenuButtonCheckoutPrice = $(
      '[data-product-menu-button-checkout-price]',
      this.$el,
    );

    // Minicart title
    this.$productMiniCartTitle = $(
      '[data-product-menu-slideout-heading="minicart-title"]',
      this.$el,
    );
    this.cartTitleText = this.$productMiniCartTitle.text();

    // Success and failure elements
    this.$productAtcFailureMessage = $(
      '[data-product-menu-addtocart-failure-message]',
      this.$el,
    );

    this.revertToDefaultState = () => this._revertToDefaultState();
    this.changeToAddToCartState = () => this._changeState(this.states.addToCart);
    this._setMaxHeight = this._setMaxHeight.bind(this);
    this._setScrollLock = this._setScrollLock.bind(this);
    this._allowTouchMove = event => event.stopPropagation();
    this._submitCheckoutForm = e => {
      const currentTarget = e.currentTarget.cloneNode();
      const form = document.createElement('form');
      form.action = currentTarget.dataset.action;
      form.method = 'POST';
      form.style.display = 'None';
      currentTarget.type = 'submit';
      form.appendChild(currentTarget);
      document.body.appendChild(form);
      currentTarget.click();
    };

    // Prime the variant data
    this._onVariantChange(this.form.variantHelper.getSelectedVariant());
    $(window).trigger('product-variant-switch.product-menu', {
      variant: this.form.variantHelper.getSelectedVariant(),
      product: this.data.product,
    });

    this._changeState(this.currentState);

    layout.onBreakpointChange(this.revertToDefaultState);
    this.dismissButton.addEventListener('click', this.revertToDefaultState);
    this.selectOptionsButton.addEventListener('click', this.changeToAddToCartState);
    this.el.addEventListener('touchmove', this._allowTouchMove);
    this.checkoutButton.addEventListener('click', this._submitCheckoutForm);

    this._setMaxHeight();
    window.addEventListener('resize', this._setMaxHeight);

    Cart.addCart(el, this.postMessage);
  }

  _setScrollLock() {
    // If cart drawer is smaller than full screen, allow scrolling.
    if (this.el.clientHeight >= window.innerHeight) {
      ScrollLock.lock(this.el);
    } else {
      ScrollLock.unlock();
    }
  }

  _changeState(state, animate = true) {
    $(window).off('touchstart.menu');
    $(window).off('touchend.menu touchcancel.menu');
    this.$el.off('touchstart.menu');
    this.$el.off('touchend.menu');

    const isDesktop = layout.isBreakpoint('L');

    if (animate) {
      let slideoutContent = this.el.querySelector(`[data-product-menu-slideout-state="${this.currentState}"]`);
      let $slideoutContent = $(slideoutContent);
      let shouldSlideoutAnimate = (
        $slideoutContent.length &&
        slideoutContent.hasAttribute('data-product-menu-slideout-no-animation')
      );

      this.$slideout.slideUp({
        duration: shouldSlideoutAnimate ? 0 : '',
        complete: () => {
          this.$slideout.removeClass('product-menu-slideout-visible');
          this.$slideout.css('display', '');

          this.currentState = state;
          this.el.setAttribute('data-product-menu-state', state);

          slideoutContent = this.el.querySelector(`[data-product-menu-slideout-state="${state}"]`);
          $slideoutContent = $(slideoutContent);

          if (!$slideoutContent.length) {
            this._setScrollLock();
            return;
          }

          shouldSlideoutAnimate = slideoutContent.hasAttribute('data-product-menu-slideout-no-animation');
          const shouldShowSlideout = (
            (
              !isDesktop &&
              !slideoutContent.hasAttribute('data-product-menu-mobile-slideout-hidden')
            ) || (
              isDesktop &&
              !slideoutContent.hasAttribute('data-product-menu-desktop-slideout-hidden')
            )
          );

          if (shouldShowSlideout) {
            this.$slideout.slideDown({
              duration: shouldSlideoutAnimate ? 0 : '',
              start: () => {
                this.$slideout.css('display', '');
                this.$slideout.addClass('product-menu-slideout-visible');
              },
              complete: this._setScrollLock,
            });
          } else {
            this._setScrollLock();
          }
        },
      });
    } else {
      this.currentState = state;
      this.el.setAttribute('data-product-menu-state', state);
      window.requestAnimationFrame(this._setScrollLock);
    }
    $(window).on('touchstart.menu', () => this._minimizeSlideout());
    $(window).on('touchend.menu touchcancel.menu', () => this._maximizeSlideout());
    this.$el.on('touchstart.menu', event => event.stopPropagation());
    this.$el.on('touchend.menu, ', event => event.stopPropagation());
  }

  _setMaxHeight() {
    // Set max height to handle changing showing/hiding address bar on mobile devices
    // const newMaxHeight = this.minimized ? 0 : window.innerHeight;
    this.el.style.maxHeight = `${this.minimized ? 0 : window.innerHeight}px`;
  }

  _minimizeSlideout() {
    if (!$(`[data-product-menu-slideout-state="${this.currentState}"]`, this.el).length) {
      return;
    }

    const height = this.slideout.getBoundingClientRect().height;
    this.$slideout.animate({ height: height / 2, opacity: 0.2 }, { queue: false, duration: 500 });
  }

  _maximizeSlideout() {
    if (!$(`[data-product-menu-slideout-state="${this.currentState}"]`, this.el).length) {
      return;
    }

    const oldHeight = this.slideout.getBoundingClientRect().height;
    this.$slideout.css('height', '');

    const height = this.slideout.getBoundingClientRect().height;
    this.$slideout.css('height', oldHeight);

    this.$slideout.animate({ height, opacity: 1 }, { queue: false, duration: 500 });
  }

  _clearFailure() {
    clearTimeout(this.errorMessageTimeout);
    this.$productAtcFailureMessage.hide(0);
  }

  _revertToDefaultState() {
    this._changeState(layout.isBreakpoint('L') ? this.defaultDesktopState : this.defaultMobileState);
  }

  closeATCSuccess() {
    if (this.currentState === 'addtocart-success') {
      this._revertToDefaultState();
    }
  }

  minimizeMenu() {
    this._revertToDefaultState();
    this.el.classList.add('menu-minimized');
    this.minimized = true;
    this._setMaxHeight();
  }

  maximizeMenu() {
    this.minimized = false;
    this.el.classList.remove('menu-minimized');
    this._setMaxHeight();
  }

  _onVariantChange(variant) {
    // ATC
    this.$el.find('[data-product-menu-button-addtocart]')
      .prop('disabled', !variant.available);

    // ATC text
    this.$el.find('[data-product-menu-button-addtocart-text]')
      .text(variant.available
        ? this.data.text.product_available
        : this.data.text.product_unavailable,
      );

    // Options
    this.form.variantHelper.$options
      .parent()
      .removeClass('option-soldout')
      .removeClass('option-selected')
      .addClass('option-disabled');

    this.form.variantHelper.$options.filter(':not(:disabled)')
      .parent()
      .removeClass('option-disabled');

    this.form.variantHelper.$options.filter('[data-soldout="true"]')
      .parent()
      .addClass('option-soldout');

    this.form.variantHelper.$options.filter(':checked')
      .parent()
      .addClass('option-selected');

    this._onQuantityChange(variant, this.form.quantity.value);
  }

  _onQuantityChange(variant, quantity) {
    const price = Shopify.formatMoney(variant.price * quantity, window.Theme.moneyFormat);

    this.$productMenuButtonAddToCartPrice
      .text(price);
  }

  _onAddToCart() {
    this.addToCartButton.classList.add('loading');
  }

  _onAtcSuccess() {
    Cart.update()
      .then(() => {
        this.$productAtcFailureMessage.hide(0);
        this._changeState(this.states.addToCartSuccess);
      })
      .finally(() => this.addToCartButton.classList.remove('loading'));
  }

  _onAtcError(response, error) {
    this.addToCartButton.classList.remove('loading');
    this.$productAtcFailureMessage.html(error);

    if (this.form.variantHelper.isDefault()) {
      this.$productAtcFailureMessage.slideDown(0);
      this.$slideout.slideDown();
    } else {
      this.$productAtcFailureMessage.slideDown();
    }

    this.errorMessageTimeout = setTimeout(() => {
      if (this.form.variantHelper.isDefault()) {
        this.$slideout.slideUp({
          start: () => {
            this.$slideout.addClass('product-menu-slideout-visible');
            this.$slideout.css('display', '');
          },
          complete: () => this.$productAtcFailureMessage.slideUp(0),
        });
      } else {
        this.$productAtcFailureMessage.slideUp();
      }
    }, 5000);
  }

  _updateMiniCartTotals(cartData) {
    if (cartData.item_count === 0) {
      this._changeState('selectoptions');
      return;
    }

    this.$productMenuButtonCheckoutPrice.text(
      Shopify.formatMoney(cartData.total_price, window.Theme.moneyFormat),
    );

    this.$productMiniCartTitle.text(`${this.cartTitleText} (${cartData.item_count})`);
  }

  onSectionMessage(name, data) {
    if (name === 'cart:update') {
      this._updateMiniCartTotals(data);
    }
    if (name === 'header-minicart:open') {
      const transitionEnded = $.Deferred();
      data(transitionEnded);
      this.$el.one('transitionend', () => transitionEnded.resolve());
      this.minimizeMenu();
    }
    if (name === 'header-minicart:close') {
      this.maximizeMenu();
    }
  }

  unload() {
    $(window).off('touchstart.menu');
    $(window).off('touchend.menu touchcancel.menu');
    this.$el.off('touchstart.menu');
    this.$el.off('touchend.menu');
    layout.offBreakpointChange(this.revertToDefaultState);
    this.dismissButton.removeEventListener('click', this.revertToDefaultState);
    this.selectOptionsButton.removeEventListener('click', this.changeToAddToCartState);
    this.el.removeEventListener('touchmove', this._allowTouchMove);
    this.checkoutButton.removeEventListener('click', this._submitCheckoutForm);
    window.removeEventListener('resize', this._setMaxHeight);
    this.form.unload();
  }
}
